import React from "react";
import { ChevronDown, Loader2 } from "lucide-react";

import { UserRole } from "@modules/authentication/roles";
import { capitalisedSentence } from "@util/string.util";
import { cn } from "~/lib/utils";
import { Badge } from "~/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { getRoleBadgeProps } from "@modules/user/userViewModel";

export interface UserRoleSelectProps {
  role: UserRole;
  roles: readonly UserRole[];
  onSelect: (role: UserRole) => void;
  isPending?: boolean;
  /** When set, the control is disabled and this is shown as a tooltip. */
  disabledReason?: string;
  userLabel: string;
}

/**
 * The user's role badge doubling as a dropdown trigger. Stateless: the caller owns
 * persistence so this stays reusable wherever a role can be changed.
 */
export const UserRoleSelect: React.FC<UserRoleSelectProps> = ({
  role,
  roles,
  onSelect,
  isPending = false,
  disabledReason,
  userLabel,
}) => {
  const isDisabled = isPending || Boolean(disabledReason);
  const badgeProps = getRoleBadgeProps(role);

  const trigger = (
    <DropdownMenuTrigger
      disabled={isDisabled}
      aria-label={`Change role for ${userLabel}`}
      className="rounded-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Badge
        {...badgeProps}
        className={cn(
          "gap-1",
          !isDisabled && "cursor-pointer",
          disabledReason && "opacity-70",
          badgeProps.className
        )}
      >
        {capitalisedSentence(role)}
        {isPending ? (
          <Loader2 className="size-3 animate-spin" aria-hidden />
        ) : (
          !disabledReason && <ChevronDown className="size-3" aria-hidden />
        )}
      </Badge>
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu>
      {disabledReason ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="inline-flex"
              tabIndex={0}
              onClick={(event) => event.stopPropagation()}
            >
              {trigger}
            </span>
          </TooltipTrigger>
          <TooltipContent>{disabledReason}</TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
      <DropdownMenuContent
        align="start"
        onClick={(event) => event.stopPropagation()}
      >
        <DropdownMenuLabel>Change role</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={role}
          onValueChange={(value) => {
            if (value !== role) {
              onSelect(value as UserRole);
            }
          }}
        >
          {roles.map((option) => (
            <DropdownMenuRadioItem key={option} value={option}>
              {capitalisedSentence(option)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
